import { create } from 'zustand';
import { axiosInstance } from '@/utils/lib/axios';
import { env } from '@/utils/constants/env';
import { errorHandler } from '@/utils/functions/errorHandler';
import { toast } from 'react-toastify';
import useCompanyProfileCompletionState from './completion.state';

export interface ITrain {
  id?: number | string;
  tenant_id?: number | string;
  org_chart_node_id?: number;
  org_chart_node?: {
    id: number;
    name: string;
  };
  type?: string;
  name?: string;
  value?: string;
  description?: string;
  attachment_file?: any;
  is_published?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ITrainPaginatedResponse {
  content: ITrain[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface TrainState {
  trains: ITrain[];
  paginatedData: ITrainPaginatedResponse | null;
  selectedTrain: ITrain | null;
  isLoading: boolean;
  error: unknown;

  createTrain: (tenantId: string | number, payload: Partial<ITrain>) => Promise<void>;
  getTrains: (
    tenantId: string | number,
    params?: {
      page?: number;
      page_size?: number;
      org_chart_node_id?: number;
      type?: string;
      name?: string;
      is_published?: boolean;
      orderBy?: string;
      order?: string;
    },
  ) => Promise<ITrainPaginatedResponse | void>;
  getTrainDetail: (tenantId: string | number, id: number | string) => Promise<ITrain | void>;
  updateTrain: (tenantId: string | number, id: number | string, payload: Partial<ITrain>) => Promise<void>;
  bulkPublishTrain: (tenantId: string | number, isPublished: boolean) => Promise<void>;
  deleteTrain: (tenantId: string | number, id: number | string) => Promise<void>;
  clearTrains: () => void;
  setTrains: (trains: ITrain[]) => void;
}

const useTrainState = create<TrainState>((set, get) => ({
  trains: [],
  paginatedData: null,
  selectedTrain: null,
  isLoading: false,
  error: null,

  setTrains: trains => set({ trains }),
  clearTrains: () => set({ trains: [], paginatedData: null }),

  createTrain: async (tenantId, payload) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}`;
      await axiosInstance.post(url, payload);

      await get().getTrains(tenantId);
      
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Training created successfully');
      set({ isLoading: false });
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getTrains: async (tenantId, params) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}`;
      const { data } = await axiosInstance.get(url, { params });

      const paginatedResponse: ITrainPaginatedResponse = {
        content: data.data?.content || [],
        totalElements: data.data?.totalElements || 0,
        totalPages: data.data?.totalPages || 0,
        size: data.data?.size || 10,
        number: data.data?.number || 0,
      };

      set({
        trains: data.data?.content || [],
        paginatedData: paginatedResponse,
        isLoading: false,
      });

      return paginatedResponse;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  getTrainDetail: async (tenantId, id) => {
    set({ isLoading: true, error: null });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/${id}`;
      const { data } = await axiosInstance.get(url);
      set({ selectedTrain: data.data, isLoading: false });
      return data.data;
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  updateTrain: async (tenantId, id, payload) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/${id}`;
      const { data } = await axiosInstance.put(url, payload);
      set(state => ({
        trains: state.trains.map(t => (t.id === id ? { ...t, ...data.data } : t)),
        selectedTrain: data.data,
        isLoading: false,
      }));
      toast.success('Training updated successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  deleteTrain: async (tenantId, id) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/${id}`;
      await axiosInstance.delete(url);
      set(state => ({
        trains: state.trains.filter(t => t.id !== id),
        isLoading: false,
      }));
      
      const completionStore = useCompanyProfileCompletionState.getState();
      completionStore.fetchCompletion();
      toast.success('Training deleted successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },

  bulkPublishTrain: async (tenantId: string | number, isPublished: boolean) => {
    set({ isLoading: true });
    try {
      const url = `${env.apiTasklistBaseUrl}/api/v1/compro/train-trainers/${tenantId}/bulk-publish`;
      await axiosInstance.put(url, { is_published: isPublished });

      set(state => ({
        trains: state.trains.map(train => ({
          ...train,
          is_published: isPublished,
        })),
        isLoading: false,
      }));

      toast.success(isPublished ? 'All trainings published successfully' : 'All trainings unpublished successfully');
    } catch (error) {
      set({ error, isLoading: false });
      errorHandler(error);
    }
  },
}));

export default useTrainState;
